"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Snowflake, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface PipeIssue {
  id: string;
  title: string;
  description: string;
  urgency: string;
  iconName: "zap" | "wrench" | "snowflake" | "flame" | "droplets";
  stripeColor: string;
}
const pipeIssues: PipeIssue[] = [{
  id: "burst",
  title: "Burst Pipe",
  description: "Sudden high-volume water release causing immediate structural flooding and rapid wall damage.",
  urgency: "Emergency",
  iconName: "zap",
  stripeColor: "bg-destructive"
}, {
  id: "broken",
  title: "Broken Pipe",
  description: "Cracks and severe structural joint fractures inside walls, basements, or under home foundations.",
  urgency: "Urgent",
  iconName: "wrench",
  stripeColor: "bg-accent"
}, {
  id: "frozen",
  title: "Frozen Pipe",
  description: "Severe ice blockages causing rapid pressure buildup and expansion risk during freeze conditions.",
  urgency: "High Risk",
  iconName: "snowflake",
  stripeColor: "bg-chart-3"
}, {
  id: "corroded",
  title: "Corroded Pipes",
  description: "Aged galvanized metal or copper deterioration leading to discolored water and restricted flow.",
  urgency: "Moderate",
  iconName: "flame",
  stripeColor: "bg-chart-2"
}, {
  id: "leaking",
  title: "Leaking Pipes",
  description: "Hidden pinhole leaks, dripping connectors, and silent moisture seepage behind drywall.",
  urgency: "Active",
  iconName: "droplets",
  stripeColor: "bg-primary"
}];
function renderIssueIcon(name: PipeIssue["iconName"]) {
  switch (name) {
    case "zap":
      return <Zap className="size-5 text-destructive" />;
    case "wrench":
      return <Wrench className="size-5 text-accent-foreground" />;
    case "snowflake":
      return <Snowflake className="size-5 text-chart-3" />;
    case "flame":
      return <Flame className="size-5 text-accent-foreground" />;
    case "droplets":
      return <Droplets className="size-5 text-primary" />;
    default:
      return <AlertTriangle className="size-5 text-primary" />;
  }
}
export default function PipeProblems() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="pipeproblems" data-nav="light" className="relative bg-background text-foreground py-16 sm:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold tracking-wide uppercase text-xs">
            Diagnostic Guide
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground font-default mb-4">
            Pipe Problems
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Plumbing pipe failures can quickly lead to severe structural damage and water loss. Identify your symptoms early to stop moisture intrusion before it spreads.
          </p>
        </div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {pipeIssues.map((issue, i) => <motion.div key={issue.id} data-index={i} variants={itemVariants} className={`h-full ${i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}`}>
              <Card className="relative h-full overflow-hidden border border-border bg-card text-card-foreground shadow-sm transition-all hover:shadow-md hover:border-primary/40 flex flex-col justify-between">
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${issue.stripeColor}`} aria-hidden="true" />
                <div>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pt-7 pb-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-secondary/80 border border-border">
                      {renderIssueIcon(issue.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium">
                      {issue.urgency}
                    </Badge>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <CardTitle className="text-xl font-bold font-default text-card-foreground mb-2">
                      {issue.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-normal">
                      {issue.description}
                    </CardDescription>
                  </CardContent>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <div className="mt-12 sm:mt-16 text-center">
          <Button asChild size="lg" className="font-semibold px-8 shadow-sm">
            <Link to="/pipe-repair/">
              View Pipe Problems
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}